import "core-js/modules/es.promise.finally";
import _toConsumableArray from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/toConsumableArray";
import _slicedToArray from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import { useReducer, useEffect, useMemo, useState, useRef } from 'react';
import highlightReducer from './highlightReducer';
import getDisplayValue from './lib/getDisplayValue';
import flattenOptions from './lib/flattenOptions';
import GroupOptions from './lib/groupOptions';
import getNewValue from './lib/getNewValue';
import getOption from './lib/getOption';
import doSearch from './search';
export default function useSelectSearch(_ref) {
  var _ref$value = _ref.value,
      defaultValue = _ref$value === void 0 ? null : _ref$value,
      _ref$disabled = _ref.disabled,
      disabled = _ref$disabled === void 0 ? false : _ref$disabled,
      _ref$multiple = _ref.multiple,
      multiple = _ref$multiple === void 0 ? false : _ref$multiple,
      _ref$search = _ref.search,
      canSearch = _ref$search === void 0 ? false : _ref$search,
      _ref$fuse = _ref.fuse,
      fuse = _ref$fuse === void 0 ? false : _ref$fuse,
      defaultOptions = _ref.options,
      _ref$onChange = _ref.onChange,
      onChange = _ref$onChange === void 0 ? function () {} : _ref$onChange,
      _ref$getOptions = _ref.getOptions,
      getOptions = _ref$getOptions === void 0 ? null : _ref$getOptions,
      _ref$allowEmpty = _ref.allowEmpty,
      allowEmpty = _ref$allowEmpty === void 0 ? true : _ref$allowEmpty,
      _ref$closeOnSelect = _ref.closeOnSelect,
      closeOnSelect = _ref$closeOnSelect === void 0 ? true : _ref$closeOnSelect;
  var ref = useRef(null);
  var flatDefaultOptions = useMemo(function () {
    return flattenOptions(defaultOptions);
  }, [defaultOptions]);

  var _useState = useState([]),
      _useState2 = _slicedToArray(_useState, 2),
      flat = _useState2[0],
      setOptions = _useState2[1];

  var _useState3 = useState([]),
      _useState4 = _slicedToArray(_useState3, 2),
      addedOptions = _useState4[0],
      setAddedOptions = _useState4[1];

  var _useState5 = useState(defaultValue),
      _useState6 = _slicedToArray(_useState5, 2),
      value = _useState6[0],
      setValue = _useState6[1];

  var option = useMemo(function () {
    var newOption = getOption(value, [].concat(_toConsumableArray(flatDefaultOptions), _toConsumableArray(addedOptions)));

    if (!newOption && !allowEmpty) {
      var _flatDefaultOptions = _slicedToArray(flatDefaultOptions, 1);

      newOption = _flatDefaultOptions[0];
    }

    return newOption;
  }, [value, flatDefaultOptions, addedOptions, allowEmpty]);

  var _useState7 = useState(''),
      _useState8 = _slicedToArray(_useState7, 2),
      search = _useState8[0],
      setSearch = _useState8[1];

  var _useState9 = useState(false),
      _useState10 = _slicedToArray(_useState9, 2),
      focus = _useState10[0],
      setFocus = _useState10[1];

  var _useState11 = useState(false),
      _useState12 = _slicedToArray(_useState11, 2),
      searching = _useState12[0],
      setSearching = _useState12[1];

  var _useReducer = useReducer(highlightReducer, -1),
      _useReducer2 = _slicedToArray(_useReducer, 2),
      highlighted = _useReducer2[0],
      setHighlighted = _useReducer2[1];

  var options = useMemo(function () {
    return GroupOptions(flat);
  }, [flat]);
  var displayValue = useMemo(function () {
    return getDisplayValue(option);
  }, [option]);

  var onBlur = function onBlur() {
    setFocus(false);
    setHighlighted(false);

    if (ref.current) {
      ref.current.blur();
    }

    if (!multiple) {
      setSearch('');
      setOptions(flatDefaultOptions);
    }
  };

  var onFocus = function onFocus() {
    return setFocus(true);
  };

  var onSelect = function onSelect(val) {
    var newOption = getOption(val, flat);
    var newOptions = getNewValue(newOption, option, multiple);
    var values = multiple ? newOptions.map(function (i) {
      return i.value;
    }) : newOptions.value;
    setAddedOptions(multiple ? newOptions : [newOptions]);
    setValue(values);
    onChange(values, newOptions);
  };

  var onMouseDown = function onMouseDown(e) {
    if (!closeOnSelect) {
      e.preventDefault();
    }

    onSelect(e.currentTarget.value);
  };

  var onKeyDown = function onKeyDown(e) {
    return setHighlighted({
      key: e.key,
      options: flat
    });
  };

  var onKeyPress = function onKeyPress(_ref2) {
    var key = _ref2.key;

    if (key === 'Enter') {
      var newOption = flat[highlighted];

      if (newOption) {
        onSelect(newOption.value);

        if (!multiple) {
          onBlur();
        }
      }
    }
  };

  var onKeyUp = function onKeyUp(_ref3) {
    var key = _ref3.key;

    if (key === 'Escape') {
      onBlur();
    }
  };

  var onSearch = function onSearch(_ref4) {
    var target = _ref4.target;
    var inputVal = target.value;
    setSearch(inputVal);
    var searchableOption = flatDefaultOptions;

    if (getOptions && inputVal.length) {
      setSearching(true);
      searchableOption = getOptions(inputVal);
    }

    Promise.resolve(searchableOption).then(function (foundOptions) {
      if (inputVal.length) {
        var newOptions = doSearch(inputVal, foundOptions, fuse);
        setOptions(newOptions === false ? foundOptions : newOptions);
      } else {
        setOptions(foundOptions);
      }
    }).catch(function () {
      return setOptions(flatDefaultOptions);
    }).finally(function () {
      return setSearching(false);
    });
  };

  var valueProps = {
    tabIndex: '0',
    readOnly: !canSearch,
    onChange: canSearch ? onSearch : null,
    onBlur: onBlur,
    onFocus: onFocus,
    onKeyPress: onKeyPress,
    onKeyDown: onKeyDown,
    onKeyUp: onKeyUp,
    ref: ref
  };
  var optionProps = {
    tabIndex: '-1',
    onMouseDown: onMouseDown
  };
  useEffect(function () {
    setValue(defaultValue);
  }, [defaultValue]);
  useEffect(function () {
    setOptions(flatDefaultOptions);
  }, [flatDefaultOptions]);
  return [{
    value: option,
    highlighted: highlighted,
    options: options,
    disabled: disabled,
    displayValue: displayValue,
    focus: focus,
    search: search,
    searching: searching
  }, valueProps, optionProps, setValue];
}